import { Box, Button, HStack, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ChevronLeftIcon, PenIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { djs } from '@duke-hq/djs';
import {
  CourseReferralModalMode,
  courseReferralModalState,
} from '../../tenantState/CourseReferralState';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { StudentWorkSchedule } from '@duke-hq/svc-api-db';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { CourseSegment } from '@duke-hq/shared-constants/enrolments';
import { getCourseDatesFromAdminCourseSchedule, toUtcStartOfDay } from '@duke-hq/lib/dates';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';

type SummaryProps = {
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
  onReferralCreationSuccess: (activationCode: RouterOutputs['courseReferral']['create']) => void;
};

export function Summary({ goToPrevStep, setStep, onReferralCreationSuccess }: SummaryProps) {
  const [supportTaskCreated, setSupportTaskCreated] = useState<boolean>(false);
  const courseReferralModalSnap = useSnapshot(courseReferralModalState);
  const toast = useToast();
  const trpcUtils = trpcTenant.useUtils();

  const { data: adminCourse, isLoading } = trpcTenant.courses.courseSchedule.useQuery(
    courseReferralModalSnap.selectedCourseAttributes.id,
  );

  const courseStartAndEndDates = useMemo(() => {
    if (!adminCourse?.schedule || isLoading) {
      return null;
    }

    try {
      return getCourseDatesFromAdminCourseSchedule({
        adminCourseScheduleItems: adminCourse.schedule,
        courseSegment:
          courseReferralModalSnap.enrolmentSettingsData.course_segment ?? CourseSegment.FULL_COURSE,
        workSchedule:
          courseReferralModalSnap.enrolmentSettingsData.work_schedule ??
          StudentWorkSchedule.FULL_TIME,
        startAt: toUtcStartOfDay(
          courseReferralModalSnap.enrolmentSettingsData.start_at_date,
          'YYYY-MM-DD',
        ),
      });
    } catch {
      // handle bad user input in the date fields gracefully
      return null;
    }
  }, [
    adminCourse,
    courseReferralModalSnap.enrolmentSettingsData.course_segment,
    courseReferralModalSnap.enrolmentSettingsData.start_at_date,
    courseReferralModalSnap.enrolmentSettingsData.work_schedule,
    isLoading,
  ]);

  const createCourseReferral = trpcTenant.courseReferral.create.useMutation({
    onSuccess: (res) => {
      toast({
        title: t`Referral created successfully`,
        status: 'success',
        duration: 3000,
      });
      onReferralCreationSuccess(res);
      void trpcUtils.courseReferral.getAll.invalidate();
      void trpcUtils.courseReferral.countByStatus.invalidate();
    },
    onError: (error) => {
      toast({
        title: t`Failed to create referral`,
        description: error.message,
        status: 'error',
        duration: 7000,
      });
    },
  });

  const updateCourseEnrolment = trpcTenant.enrolments.updateCourseEnrolment.useMutation({
    onSuccess: () => {
      toast({
        title: t`Course enrolment updated successfully`,
        status: 'success',
        duration: 3000,
      });
      void trpcUtils.enrolments.all.invalidate();
      void trpcUtils.enrolments.byIdWithResourceStatus.invalidate();
      courseReferralModalState.closeModal();
    },
    onError: (error) => {
      toast({
        title: t`Failed to update course enrolment`,
        description:
          error.message ||
          t`An error occurred. Please try again and if the problem persists, contact support.`,
        status: 'error',
        duration: 7000,
      });
    },
  });

  const createSupportTaskMutation =
    trpcTenant.support.createSupersededCourseTransferTask.useMutation({
      onSuccess: () => {
        toast({
          title: t`Support task created successfully.`,
          description: t`We couldn't add the second semester at this point, but a support task has been created to assist you with next steps.`,
          status: 'info',
          duration: 7000,
        });
        setSupportTaskCreated(true);
      },
      onError: (error) => {
        toast({
          title: t`Failed to create support task`,
          description:
            error.message ||
            t`An error occurred. Please try again and if the problem persists, contact support.`,
          status: 'error',
          duration: 15000,
        });
      },
      onSettled: () => {
        courseReferralModalState.closeModal();
      },
    });

  const handleReferralCreateSubmit = async () => {
    const { selectedCourseAttributes, enrolmentSettingsData } = courseReferralModalSnap;
    if (enrolmentSettingsData.selected_consultant && enrolmentSettingsData.selected_site) {
      const reqPayload = {
        course_id: selectedCourseAttributes.id,
        site_id: enrolmentSettingsData.selected_site.site_id,
        start_at_date: enrolmentSettingsData.start_at_date,
        enrolment_contract_type: enrolmentSettingsData.enrolment_contract_type,
        contract_category: enrolmentSettingsData.contract_category,
        work_schedule: enrolmentSettingsData.work_schedule as StudentWorkSchedule,
        course_segment: enrolmentSettingsData.course_segment,
        consultant_id: enrolmentSettingsData.selected_consultant.consultant_id,
        external_id: enrolmentSettingsData.external_id,
        user_timezone: djs.tz.guess(),
      };

      await createCourseReferral.mutateAsync(reqPayload);
    }
  };

  const handleCreateSupportTask = async () => {
    const enrolmentId = courseReferralModalSnap.enrolmentSettingsData.enrolmentId;
    if (enrolmentId == null) {
      return;
    }
    await createSupportTaskMutation.mutateAsync({
      enrolmentId: Number(enrolmentId),
    });
  };

  const handleUpdateCourseEnrolmentSubmit = async () => {
    const { enrolmentSettingsData } = courseReferralModalSnap;

    if (enrolmentSettingsData.enrolmentId != null && enrolmentSettingsData.studentId != null) {
      const reqPayload = {
        enrolment_id: Number(enrolmentSettingsData.enrolmentId),
        start_at_date: enrolmentSettingsData.start_at_date,
        ...(enrolmentSettingsData.semester_2_start_at_date != null
          ? {
              semester_2_start_at_date: enrolmentSettingsData.semester_2_start_at_date,
            }
          : {}),
        user_id: enrolmentSettingsData.studentId,
        enrolment_contract_type: enrolmentSettingsData.enrolment_contract_type,
        contract_category: enrolmentSettingsData.contract_category,
        course_segment:
          courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER
            ? 'Full course'
            : enrolmentSettingsData.course_segment,
        work_schedule: enrolmentSettingsData.work_schedule,
        semesterTwoExternalId: enrolmentSettingsData.semesterTwoExternalId,
      };

      await updateCourseEnrolment.mutateAsync(reqPayload);
    }
  };

  const getCoursePrice = useCallback(() => {
    if (
      courseReferralModalSnap.enrolmentSettingsData.course_segment ===
        CourseSegment.FIRST_SEMESTER_ONLY &&
      (courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW ||
        courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE)
    ) {
      return parseFloat(courseReferralModalSnap.selectedCourseAttributes.price_aud.toString()) / 2;
    }
    return courseReferralModalSnap.selectedCourseAttributes.price_aud;
  }, [
    courseReferralModalSnap.enrolmentSettingsData.course_segment,
    courseReferralModalSnap.selectedCourseAttributes.price_aud,
    courseReferralModalSnap.mode,
  ]);

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(0)}>
            <Trans>Course</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField
              name={courseReferralModalSnap.selectedCourseAttributes.identifier}
              value={courseReferralModalSnap.selectedCourseAttributes.title}
            />
          </SimpleGrid>
          <SingleField name={t`Cost`} value={`$${getCoursePrice()}`} />
        </Box>
        <Box w="full">
          <SummarySectionHeading onEditClick={() => setStep(2)}>
            <Trans>Enrolment settings</Trans>
          </SummarySectionHeading>
          <SimpleGrid columns={2} w="full">
            <SingleField
              name={<Trans>Site</Trans>}
              value={`${courseReferralModalSnap?.enrolmentSettingsData?.selected_site?.site_name}`}
            />
            <SingleField
              name={<Trans>Consultant</Trans>}
              value={
                courseReferralModalSnap?.enrolmentSettingsData?.selected_consultant?.consultant_name
              }
            />
            <SingleField
              name={<Trans>Start date</Trans>}
              value={
                courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW ||
                courseReferralModalSnap.mode ===
                  CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE
                  ? djs(courseReferralModalSnap.enrolmentSettingsData.start_at_date).format(
                      'dddd, DD MMMM YYYY',
                    )
                  : djs(
                      courseReferralModalSnap.enrolmentSettingsData.semester_2_start_at_date,
                    ).format('dddd, DD MMMM YYYY')
              }
            />
            <SingleField
              name={<Trans>Approved purchase order/Unique identifier</Trans>}
              value={
                courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW ||
                courseReferralModalSnap.mode ===
                  CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE
                  ? courseReferralModalSnap.enrolmentSettingsData.external_id
                  : courseReferralModalSnap.enrolmentSettingsData.semesterTwoExternalId
              }
            />
            <SingleField
              name={<Trans>Contract type</Trans>}
              value={courseReferralModalSnap.enrolmentSettingsData.enrolment_contract_type}
            />
            <SingleField
              name={<Trans>Contract category</Trans>}
              value={courseReferralModalSnap.enrolmentSettingsData.contract_category}
            />
            <SingleField
              name={<Trans>Course Segment</Trans>}
              value={
                courseReferralModalSnap.mode === CourseReferralModalMode.ADD_SECOND_SEMESTER
                  ? t`Second semester`
                  : courseReferralModalSnap.enrolmentSettingsData.course_segment
              }
            />

            <SingleField
              name={<Trans>Enrolment pace</Trans>}
              value={EnrolmentPace[courseReferralModalSnap.enrolmentSettingsData.work_schedule]}
            />
          </SimpleGrid>
          {courseStartAndEndDates?.courseEndDate ? (
            <Text
              color="fg.emphasis"
              textStyle="sm"
              textAlign="left"
              fontWeight="normal"
              w="full"
              mt={2}
            >
              <Trans>Course is expected to end by</Trans>{' '}
              {courseStartAndEndDates?.courseEndDate.long}
            </Text>
          ) : null}
        </Box>
      </FormContentContainer>
      <FormFooterContainer>
        {courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW ||
        courseReferralModalSnap.mode === CourseReferralModalMode.CREATE_NEW_FOR_SELECTED_COURSE ? (
          <Button onClick={handleReferralCreateSubmit} isLoading={createCourseReferral.isPending}>
            <Trans>Create Referral</Trans>
          </Button>
        ) : courseReferralModalSnap.selectedCourseAttributes.status ===
          CourseStatusSchema.enum.SUPERSEDED ? (
          <Button
            onClick={handleCreateSupportTask}
            isLoading={createSupportTaskMutation.isPending}
            loadingText={t`Creating task...`}
            isDisabled={createSupportTaskMutation.isPending || supportTaskCreated}
          >
            <Trans>Create Support Task</Trans>
          </Button>
        ) : (
          <Button
            onClick={handleUpdateCourseEnrolmentSubmit}
            isLoading={updateCourseEnrolment.isPending}
          >
            <Trans>Update Enrolment</Trans>
          </Button>
        )}
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}

function SummarySectionHeading({
  children,
  onEditClick,
}: {
  children: React.ReactNode;
  onEditClick: () => void;
}): React.JSX.Element {
  return (
    <HStack justifyContent="space-between" w="full">
      <Text fontSize="md" fontWeight="semibold" lineHeight="6" color="fg.emphasis">
        {children}
      </Text>
      <Button variant="ghost" onClick={onEditClick} leftIcon={<PenIcon size="20px" />} size="sm">
        <Trans>Edit</Trans>
      </Button>
    </HStack>
  );
}

enum EnrolmentPace {
  FULL_TIME = 'Full time',
  PART_TIME = 'Part time',
}
